/**
 * Enhanced Token Cost Calculator for File Attachments
 * Now uses admin dashboard config for consistent pricing
 * Aligned with Gemini API costs (258 tokens per image tile)
 */

import { loadTokenCostConfig, calculateAttachmentCost } from './advancedTokenCostSystem';

export interface TokenCostBreakdown {
    baseReadingCost: number;
    complexityMultiplier: number;
    instructionCost: number;
    multiFileMultiplier: number;
    totalCost: number;
    breakdown: {
        label: string;
        value: number;
    }[];
}

/**
 * Calculate base reading cost using admin config
 * Now properly aligned with actual charging
 */
function calculateBaseReadingCost(file: File): number {
    const config = loadTokenCostConfig();
    return calculateAttachmentCost(file.size, config);
}

/**
 * Complexity multiplier removed - admin config handles all cost calculation
 * Keeping function for backward compatibility
 */
function estimateComplexityMultiplier(file: File): number {
    return 1.0; // Admin config now handles all complexity
}

/**
 * Instruction cost removed - attachments charged separately
 * Message text is charged during message processing
 */
function calculateInstructionCost(instruction: string): number {
    return 0; // Instructions are charged separately in message processing
}

/**
 * Multi-file multiplier removed - each file charged independently
 */
function calculateMultiFileMultiplier(fileCount: number): number {
    return 1.0; // Each file is charged separately per admin config
}

/**
 * Main function to calculate total token cost for file upload
 */
export function calculateAttachmentTokenCost(
    files: File[],
    userInstruction: string = ''
): TokenCostBreakdown {
    if (files.length === 0) {
        return {
            baseReadingCost: 0,
            complexityMultiplier: 1,
            instructionCost: 0,
            multiFileMultiplier: 1,
            totalCost: 0,
            breakdown: []
        };
    }

    // Calculate base reading cost for all files
    const baseReadingCost = files.reduce((total, file) => {
        return total + calculateBaseReadingCost(file);
    }, 0);

    // Calculate average complexity multiplier
    const avgComplexityMultiplier = files.reduce((total, file) => {
        return total + estimateComplexityMultiplier(file);
    }, 0) / files.length;

    // Calculate instruction cost
    const instructionCost = calculateInstructionCost(userInstruction);

    // Calculate multi-file multiplier
    const multiFileMultiplier = calculateMultiFileMultiplier(files.length);

    // Total cost - simply sum all file costs (admin config handles complexity)
    const totalCost = baseReadingCost;

    // Build breakdown for display - show individual file costs
    const config = loadTokenCostConfig();
    const breakdown = files.map((file, idx) => ({
        label: `${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
        value: calculateAttachmentCost(file.size, config)
    }));
    
    // Add config info
    breakdown.push({
        label: `Admin Config: ${config.attachmentSizeCosts.costPerMB}/MB, Min: ${config.attachmentSizeCosts.minimumCost}, Max: ${config.attachmentSizeCosts.maximumCost}`,
        value: 0
    });

    return {
        baseReadingCost,
        complexityMultiplier: avgComplexityMultiplier,
        instructionCost,
        multiFileMultiplier,
        totalCost,
        breakdown
    };
}
